import { cn } from "@/lib/utils"

interface Agent {
  id: string
  name: string
  role: string
  color: string
  skills: string[]
}

interface Props {
  agent: Agent
  active: boolean
  task?: string
}

/**
 * IT Crowd-style pixel-art avatars — hand-crafted 16x16 sprites for each agent.
 * Each character is a recognizable pixel-art person with distinct hair, face,
 * clothing, pose, desk, and props — inspired by IT Crowd / Habbo Hotel aesthetics.
 *
 * Palette legend (varies per sprite, see each function):
 *   . = transparent
 *   S = skin tone         s = skin shadow/dark
 *   H = hair color        h = hair shadow/dark
 *   C = clothing (agent)  c = clothing shadow
 *   E = eyes (dark)       M = mouth / smile
 *   W = white / highlight P = pink / accent
 *   K = desk wood         k = desk top surface
 *   X = monitor frame     Q = screen glow
 *   B = black / outline   G = glasses / goggles
 *   T = tool / accessory  D = dark accent
 *   R = chair back        L = lighter accent
 *   O = secondary prop    N = nose / detail
 *   Y = headset / gear    U = uniform detail
 *   V = visor / hat brim  A = armor / badge
 *   F = belt / strap      Z = lab equipment
 */

// ── Shared palette constants ───────────────────────────────────────────────
const SKIN      = "#f5c5a3"
const SKIN_SHD  = "#d9a67e"
const HAIR_BLK  = "#2d2320"
const HAIR_BRN  = "#6b3f23"
const HAIR_BLD  = "#d4a843"
const HAIR_RED  = "#c04030"
const HAIR_GRY  = "#8a8a8a"
const HAIR_PNKB = "#2d2320"   // base for pink-highlighted hair
const EYES      = "#1a1a2e"
const MOUTH     = "#c47060"
const DESK      = "#6b5b4f"
const DESK_TOP  = "#8b7b6f"
const MONITOR   = "#1e293b"
const SCREEN    = "#60a5fa"
const WHITE     = "#f0f0f0"
const BLACK     = "#1a1a1a"
const CHAIR     = "#4a4040"
const CHAIR_LT  = "#5c5050"

type SpriteData = {
  grid: string[]
  palette: Record<string, string>
}

// ═══════════════════════════════════════════════════════════════════════════
// 1. SENTRY — Alert operator. Headset on. Purple vest over white shirt.
//    Two monitors on desk. Focused expression with slight frown.
// ═══════════════════════════════════════════════════════════════════════════
function spriteSentry(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "....YHHHHY......",  //  1  headset band + hair
      "....HHHHHH......",  //  2  hair top
      "...HHHHHHH......",  //  3  hair side
      "...YHSSSHY......",  //  4  headset earpieces + face
      "...YSEESEY......",  //  5  eyes
      "....SSMSSS......",  //  6  mouth
      "....WCCCW.......",  //  7  white shirt collar + vest
      "...WCCCCCCW.....",  //  8  vest body
      "..RSSCCCSSR.....",  //  9  arms out, chair
      "..RSSCCCCSR.....",  // 10  chair + hands on desk
      "..R.kkkkkkkkk...",  // 11  desk top surface
      "..R.KKKKKKKKKK..",  // 12  desk body
      "....KXXKKXXKK...",  // 13  two monitors
      "....KQQKKQQKK...",  // 14  two screens
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH,
      C: color, W: WHITE, Y: "#888888",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. FORGE — Burly build. Orange hard hat with brim. Broad shoulders.
//    Confident smirk. Thick arms resting on desk.
// ═══════════════════════════════════════════════════════════════════════════
function spriteForge(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "...VCCCCCCV.....",  //  1  hard hat brim
      "....CCCCCC......",  //  2  hard hat dome
      "....HHHHHH......",  //  3  hair under hat
      "...HHSSSSHH.....",  //  4  sideburns + face
      "....SEESSS......",  //  5  eyes, confident
      "....SSMSS.......",  //  6  smirk
      "...cCCCCCCc.....",  //  7  coveralls collar
      "..cCCCCCCCCc....",  //  8  broad torso
      ".RSSCCCCCCSSR...",  //  9  beefy arms, chair
      ".RSSSCCCCSSRR...",  // 10  big arms on desk
      ".RR.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KXXKKKKKKK..",  // 13  monitor
      "....KQQKKTKTKK..",  // 14  screen + tools on desk
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BRN, S: SKIN, E: EYES, M: MOUTH,
      C: color, c: "#c07808", V: "#333333",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN,
      R: CHAIR, T: "#777777",
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. CACHE — Lean, energetic. Wild spiky red hair sticking up.
//    Red shirt. Leaning forward eagerly. One hand up.
// ═══════════════════════════════════════════════════════════════════════════
function spriteCache(color: string): SpriteData {
  return {
    grid: [
      "...H.HH.HH.H....",  //  0  spiky hair tips
      "....HHHHHH......",  //  1  hair mass
      "...HHHHHHHH.....",  //  2  wild hair
      "...HHHHHHH......",  //  3  hair over forehead
      "....HSSSHH......",  //  4  face, hair side
      "....SEESS.......",  //  5  wide eyes
      ".....SMS........",  //  6  grin
      "....CCCCCC......",  //  7  shirt
      "...CCCCCCCC.....",  //  8  shirt body
      "..RSS.CCCC.S....",  //  9  one arm up, chair
      "..R.S.CCCC.S....",  // 10  animated pose
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KXXKKKKKK...",  // 13  monitor
      "....KQQKKKKKK...",  // 14  screen
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_RED, S: SKIN, E: EYES, M: MOUTH,
      C: color, K: DESK, k: DESK_TOP,
      X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. VAULT — Distinguished elder. Glasses. Neat combed gray hair.
//    Green sweater with white collar. Hands folded. Tidy desk.
// ═══════════════════════════════════════════════════════════════════════════
function spriteVault(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "................",  //  1
      ".....HHHH.......",  //  2  neat hair
      "....HHHHHH......",  //  3  hair
      "....HSSSSH......",  //  4  face
      "...GSEESGB......",  //  5  glasses + eyes (B=bridge)
      "....SSNSS.......",  //  6  nose detail + closed mouth
      "....WCCCCW......",  //  7  white collar + sweater
      "...CCCCCCCC.....",  //  8  sweater body
      "..RSSSCCSSSR....",  //  9  hands folded
      "..RRSSSSSSR.....",  // 10  chair + lap
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KKKKXXKKK...",  // 13  single monitor
      "....KKKKQQKKK...",  // 14  screen
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_GRY, S: SKIN, E: EYES, N: SKIN_SHD, M: MOUTH,
      C: color, G: "#555555", B: "#555555", W: WHITE,
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. GATEWAY — Military bearing. Peaked cap. Blue uniform with epaulettes.
//    Rigid upright posture. Badge on chest.
// ═══════════════════════════════════════════════════════════════════════════
function spriteGateway(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "...DCCCCCD......",  //  1  peaked cap brim
      "....CCCCCC......",  //  2  cap dome
      "....DDDDDD......",  //  3  cap band
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  stern eyes
      ".....SMSS.......",  //  6  mouth
      "...UCCWCCCU.....",  //  7  uniform collar + badge W
      "...UCCCCCCCU....",  //  8  uniform torso
      "..RSSCCCCSSSR...",  //  9  arms at sides, chair
      "..RRSSCCSSRR....",  // 10  chair
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KKKKKKKKKK..",  // 13  plain desk (no monitor)
      "....KKKKKKKKKK..",  // 14
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, E: EYES, M: MOUTH,
      C: color, D: "#1e3a6f", U: "#1e3a6f", W: "#fbbf24",
      K: DESK, k: DESK_TOP, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. PIXEL — Creative designer. Long side-swept hair with pink streaks.
//    Stylish black top with pink scarf. Drawing tablet on desk.
// ═══════════════════════════════════════════════════════════════════════════
function spritePixel(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "...HPHHHHH......",  //  1  side-swept hair, pink streak
      "..HHPHHHHHH.....",  //  2  long hair
      "..HHHHHHHHH.....",  //  3  hair falling past face
      "..HH.SSSSHH.....",  //  4  face framed by hair
      "....SEESSH......",  //  5  eyes, hair over one side
      ".....SMSS.......",  //  6  smile
      "....PCCCCP......",  //  7  pink scarf + black top
      "...CCCCCCCC.....",  //  8  top body
      "..RSSCCCCSS.....",  //  9  arms, one reaching
      "..RR.SCCCS.S....",  // 10  hand on tablet
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KXXKTTTTK...",  // 13  monitor + tablet
      "....KQQKTTTTK...",  // 14  screen + tablet screen
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_PNKB, P: color, S: SKIN, E: EYES, M: MOUTH,
      C: "#2a2a2a", K: DESK, k: DESK_TOP,
      X: MONITOR, Q: SCREEN, R: CHAIR, T: "#d946ef",
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 7. WRENCH — Mechanic / ops. Orange jumpsuit. Cap backwards.
//    Tool belt with wrench. Desk has scattered tools.
// ═══════════════════════════════════════════════════════════════════════════
function spriteWrench(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      ".....DDDDDD.....",  //  1  backwards cap
      "....DDHHHDD.....",  //  2  cap + hair poking out
      "....HHHHHH......",  //  3  hair
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  eyes
      ".....SMSS.......",  //  6  grin
      "...FCCCCCCF.....",  //  7  jumpsuit + belt
      "...CCCCCCCC.....",  //  8  jumpsuit body
      "..RSSFTCCFSSR...",  //  9  tool belt detail + arms
      "..RRSSCCSSRR....",  // 10  chair
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KTTKKXXKK...",  // 13  tools on desk + monitor
      "....KTTKKQQKK...",  // 14  tools + screen
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BRN, S: SKIN, E: EYES, M: MOUTH,
      C: color, D: "#333333", F: "#555555", T: "#999999",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 8. SHIELD — Serious protector. Buzz cut. Purple-accented dark armor
//    with shoulder pauldrons. Gold badge on chest. Imposing.
// ═══════════════════════════════════════════════════════════════════════════
function spriteShield(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "................",  //  1
      ".....HHHH.......",  //  2  buzz cut
      "....HHHHHH......",  //  3  short hair
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  stern eyes
      ".....SMSS.......",  //  6  tight mouth
      "..AACCWCCAA.....",  //  7  pauldrons + badge
      "..AACCCCCCCAA...",  //  8  armored torso
      "..RSSCCCCCSSR...",  //  9  arms, chair
      "..RRSSCCSCRR....",  // 10  chair + lap
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KKKKKKXXK...",  // 13  monitor right side
      "....KKKKKKQQK...",  // 14  screen
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, E: EYES, M: MOUTH,
      C: color, A: "#3b1d6e", W: "#fbbf24",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 9. SCOPE — Scientist. Goggles pushed up on forehead. Teal lab coat
//    over white shirt. Blond hair. Microscope + beakers on desk.
// ═══════════════════════════════════════════════════════════════════════════
function spriteScope(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "....GHHHHG......",  //  1  goggles on forehead
      "....HHHHHH......",  //  2  blond hair
      "....HHHHHHH.....",  //  3  hair
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  curious eyes
      ".....SMSS.......",  //  6  small smile
      "...WWWWWWWW.....",  //  7  lab coat collar (white)
      "...CCCCCCCC.....",  //  8  teal lab coat body
      "..RSSCCCCSS.....",  //  9  arms, one reaching
      "..RR.SCCCS.S....",  // 10  hand at microscope
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KXXKZZKZK...",  // 13  monitor + microscope + beaker
      "....KQQKZZKZK...",  // 14  screen + equipment
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLD, G: "#66d4d4", S: SKIN, E: EYES, M: MOUTH,
      C: color, W: WHITE, Z: "#0d9488",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 10. COMMANDER — Calm authority. Short cropped hair, Air Force bearing.
//     Gold-trimmed dark jacket with star insignia. Three monitors on desk.
//     Hands steepled. Radiates composure.
// ═══════════════════════════════════════════════════════════════════════════
function spriteCommander(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "................",  //  1
      ".....HHHH.......",  //  2  short cropped hair
      "....HHHHHH......",  //  3  hair
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  steady eyes
      ".....SMSS.......",  //  6  composed mouth
      "...TCCWCCCT.....",  //  7  gold trim + star badge
      "...CCCCCCCC.....",  //  8  dark jacket body
      "..RSSCCCCSSR....",  //  9  hands steepled, chair
      "..RRSSSSSSR.....",  // 10  chair
      "..R.kkkkkkkkk...",  // 11  desk top
      "....KKKKKKKKKK..",  // 12  desk body
      "....KXXKXXKXXK..",  // 13  three monitors
      "....KQQKQQKQQK..",  // 14  three screens
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, E: EYES, M: MOUTH,
      C: "#1e293b", T: color, W: "#fbbf24",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Sprite lookup
// ═══════════════════════════════════════════════════════════════════════════
// Dennis — Founder. Wavy hair past ears, round glasses, orange headphones,
// light shirt, whiteboard behind. Based on real photos. Thumbs up energy.
function spriteDennis(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "...HHHHHHHH.....",  //  1  longer wavy hair top
      "..HHHHHHHHH.....",  //  2  hair volume, past ears
      "..HH.HHHH.HH...",  //  3  hair framing face
      "..HGSSSSGH......",  //  4  round glasses + face
      "...GSEESGB......",  //  5  glasses + eyes + bridge
      "....SSNSS.......",  //  6  nose, subtle smile
      "....SSMSSS......",  //  7  smile
      "..YYWCCCCWYY....",  //  8  orange headphones + collar
      "...CCCCCCCC.....",  //  9  light shirt body
      "..RSSCCCCSSR....",  // 10  arms, chair
      "..R.kkkkkkkkk...",  // 11  desk top
      "..R.KKKKKKKKKK..",  // 12  desk body
      "....KXXKKXXKK...",  // 13  two monitors
      "....KQQKKQQKK...",  // 14  two screens (whiteboard vibes)
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: "#c49a6c", s: "#a07848", E: EYES, M: MOUTH, N: "#a07848",
      C: "#b0c4de", G: "#b08840", B: "#b08840", W: WHITE, Y: "#f97316",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Albert — CPO. Short neat hair, clean-shaven, warm smile, polo shirt,
// coffee mug on desk. Product-minded, approachable, family man.
// ═══════════════════════════════════════════════════════════════════════════
function spriteAlbert(color: string): SpriteData {
  return {
    grid: [
      "................",  //  0
      "................",  //  1
      ".....HHHH.......",  //  2  short neat hair
      "....HHHHHH......",  //  3  hair
      "....HSSSSH......",  //  4  face
      "....SSEESS......",  //  5  warm eyes
      "....SSMSSS......",  //  6  smile
      "....WCCCCW......",  //  7  polo collar
      "...CCCCCCCC.....",  //  8  polo shirt body
      "..RSSCCCCSSR....",  //  9  arms, chair
      "..RRSSSSSSR.....",  // 10  chair + hands
      "..R.kkkkkkkkk...",  // 11  desk top
      "..R.KKKKKKKKKK..",  // 12  desk body
      "....KXXKKKTKK...",  // 13  monitor + coffee mug
      "....KQQKKKTKK...",  // 14  screen + mug
      "....KKKKKKKKKK..",  // 15  desk base
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, E: EYES, M: MOUTH,
      C: color, W: WHITE, T: "#8b4513",
      K: DESK, k: DESK_TOP, X: MONITOR, Q: SCREEN, R: CHAIR,
    },
  }
}

function getSpriteForAgent(name: string, color: string): SpriteData {
  switch (name) {
    case "Commander": return spriteCommander(color)
    case "Sentry":  return spriteSentry(color)
    case "Forge":   return spriteForge(color)
    case "Cache":   return spriteCache(color)
    case "Vault":   return spriteVault(color)
    case "Gateway": return spriteGateway(color)
    case "Pixel":   return spritePixel(color)
    case "Wrench":  return spriteWrench(color)
    case "Shield":  return spriteShield(color)
    case "Scope":   return spriteScope(color)
    case "Dennis":  return spriteDennis(color)
    case "Albert":  return spriteAlbert(color)
    default:        return spriteSentry(color)
  }
}

/**
 * IT Crowd-style pixel-art avatar rendered as an inline SVG.
 *
 * Each of the nine agents has a unique hand-crafted 16x16 sprite depicting
 * a recognisable pixel-art person sitting at a desk — with distinct hair,
 * face, clothing, accessories, and props.  The `name` prop selects the
 * sprite; the `color` prop tints the character's signature clothing.
 */
function PixelAvatar({ name, color, size = 32, className }: { name: string; color: string; size?: number; className?: string }) {
  const { grid, palette } = getSpriteForAgent(name, color)
  const GRID = 16
  const cellSize = size / GRID

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${GRID} ${GRID}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`${name} avatar`}
      className={className}
    >
      {/* subtle tinted background */}
      <rect width={GRID} height={GRID} rx={0.5} fill={color + "18"} />
      {grid.map((row, y) =>
        Array.from(row).map((ch, x) => {
          if (ch === "." || ch === " ") return null
          const fill = palette[ch]
          if (!fill) return null
          return (
            <rect
              key={`${y}-${x}`}
              x={x}
              y={y}
              width={1}
              height={1}
              fill={fill}
            />
          )
        })
      )}
    </svg>
  )
}

export default function AgentCard({ agent, active, task }: Props) {
  const isWorking = active && !!task

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300",
        active
          ? "bg-card border border-border ring-1 ring-offset-0"
          : "bg-card/30 border border-transparent opacity-60"
      )}
      style={active ? { borderColor: agent.color + "30", boxShadow: `0 0 0 1px ${agent.color}40` } : {}}
    >
      <div className="relative shrink-0">
        <PixelAvatar name={agent.name} color={agent.color} size={36} />
        {active && (
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background",
              isWorking && "animate-pulse"
            )}
            style={{ backgroundColor: agent.color }}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-gray-200">{agent.name}</span>
          {isWorking ? (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-mono animate-pulse"
              style={{ backgroundColor: agent.color + "25", color: agent.color }}
            >
              working
            </span>
          ) : active ? (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: agent.color + "20", color: agent.color }}
            >
              active
            </span>
          ) : null}
        </div>
        {isWorking ? (
          <div className="text-[11px] truncate" style={{ color: agent.color + "bb" }} title={task}>
            {task}
          </div>
        ) : (
          <div className="text-[11px] text-gray-500 truncate">{agent.role}</div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Face-only pixel portraits — 10x10 grids showing head, neck, shoulders.
// Used in chat bubbles and compact views.
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// Face-only pixel portraits — 12x12 grids with more detail.
// Head, hair, facial features (eyes, nose, chin), neck, and shoulders.
// ═══════════════════════════════════════════════════════════════════════════

function portraitSentry(color: string): SpriteData {
  return {
    grid: [
      "..YHHHHHY...",  // headset band
      "..HHHHHH....",  // hair top
      ".HHHHHHHH...",  // hair volume
      ".YHSSSSHY...",  // headset earpieces + face
      ".YSESSEEY...",  // eyes with whites
      "..SsNNsS....",  // nose
      "..SSMMSS....",  // mouth
      "...sSsS.....",  // chin + neck
      "..WCCCCW....",  // collar
      ".WCCCCCCW...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, W: WHITE, Y: "#888888",
    },
  }
}

function portraitForge(color: string): SpriteData {
  return {
    grid: [
      ".VCCCCCCV...",  // hard hat brim
      "..CCCCCC....",  // hard hat dome
      "..CCCCCC....",  // hard hat
      ".HHSSSSSHH..",  // sideburns + face
      "..SEESSEE...",  // bold eyes
      "..SsNNsS....",  // nose
      "..SSMMS.....",  // smirk
      "...sSsS.....",  // chin + neck
      ".cCCCCCCc...",  // coverall collar
      "cCCCCCCCCc..",  // broad shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BRN, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, c: "#c07808", V: "#333333",
    },
  }
}

function portraitCache(color: string): SpriteData {
  return {
    grid: [
      ".H..HH..H..",  // spiky tips
      "..HHHHHH....",  // hair mass
      ".HHHHHHHH...",  // wild volume
      ".HHHHHHHH...",  // forehead hair
      "..HSSSSHH...",  // face, hair side
      "..SEESEE....",  // wide excited eyes
      "..SsNNsS....",  // nose
      "...SMMMS....",  // big grin
      "...sSsS.....",  // neck
      "..CCCCCC....",  // shirt
      ".CCCCCCCC...",
      "............",
    ],
    palette: {
      H: HAIR_RED, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color,
    },
  }
}

function portraitVault(color: string): SpriteData {
  return {
    grid: [
      "............",
      "...HHHH.....",  // neat combed hair
      "..HHHHHH....",  // hair
      "..HSSSSH....",  // face
      ".GSEEESGS...",  // glasses + eyes + bridge
      "..SsNNsS....",  // nose
      "...SMMS.....",  // mouth
      "...sSsS.....",  // chin
      "..WCCCCW....",  // white collar + sweater
      ".CCCCCCCC...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_GRY, S: SKIN, s: SKIN_SHD, E: EYES, N: SKIN_SHD, M: MOUTH,
      C: color, G: "#555555", W: WHITE,
    },
  }
}

function portraitGateway(color: string): SpriteData {
  return {
    grid: [
      ".DCCCCCD....",  // peaked cap brim
      "..CCCCCC....",  // cap dome
      "..DDDDDD....",  // cap band
      "..HSSSSH....",  // face
      "..SEESES....",  // stern eyes
      "..SsNNsS....",  // nose
      "...SMMS.....",  // tight mouth
      "...sSsS.....",  // neck
      ".UCCWCCCU...",  // uniform + badge
      "UCCCCCCCU...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, D: "#1e3a6f", U: "#1e3a6f", W: "#fbbf24",
    },
  }
}

function portraitPixel(color: string): SpriteData {
  return {
    grid: [
      ".HPHHHHH....",  // side-swept, pink streak
      "HHPHHHHHHH..",  // long flowing hair
      "HHHHHHHHH...",  // volume
      "HH.SSSSHH...",  // face framed
      "..SEESEHH...",  // eyes, hair over side
      "..SsNNsS....",  // nose
      "..SSMMSS....",  // smile
      "...sSsS.....",  // neck
      "..PCCCCP....",  // scarf + top
      ".CCCCCCCC...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_PNKB, P: color, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: "#2a2a2a",
    },
  }
}

function portraitWrench(color: string): SpriteData {
  return {
    grid: [
      "...DDDDDD...",  // backwards cap
      "..DDHHHDD...",  // cap + hair
      "..HHHHHH....",  // hair
      "..HSSSSH....",  // face
      "..SEESES....",  // eyes
      "..SsNNsS....",  // nose
      "...SMMS.....",  // grin
      "...sSsS.....",  // neck
      ".FCCCCCCF...",  // jumpsuit + belt
      ".CCCCCCCC...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BRN, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, D: "#333333", F: "#555555",
    },
  }
}

function portraitShield(color: string): SpriteData {
  return {
    grid: [
      "............",
      "...HHHH.....",  // buzz cut
      "..HHHHHH....",  // short hair
      "..HSSSSH....",  // face
      "..SEESES....",  // stern eyes
      "..SsNNsS....",  // nose
      "...SMMS.....",  // tight mouth
      "...sSsS.....",  // neck
      "AACCWCCAA...",  // pauldrons + badge
      "AACCCCCAA...",  // armored shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, A: "#3b1d6e", W: "#fbbf24",
    },
  }
}

function portraitScope(color: string): SpriteData {
  return {
    grid: [
      "..GHHHHG....",  // goggles on forehead
      "..HHHHHH....",  // blond hair
      "..HHHHHHH...",  // hair volume
      "..HSSSSH....",  // face
      "..SEESEE....",  // curious wide eyes
      "..SsNNsS....",  // nose
      "...SMMS.....",  // smile
      "...sSsS.....",  // neck
      ".WWWWWWWW...",  // lab coat collar
      ".CCCCCCCC...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLD, G: "#66d4d4", S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, W: WHITE,
    },
  }
}

function portraitCommander(color: string): SpriteData {
  return {
    grid: [
      "............",
      "...HHHH.....",  // short cropped hair
      "..HHHHHH....",  // hair
      "..HSSSSH....",  // face
      "..SEESES....",  // steady composed eyes
      "..SsNNsS....",  // nose
      "...SMMS.....",  // composed mouth
      "...sSsS.....",  // chin
      ".TCCWCCCT...",  // gold trim + star badge
      ".CCCCCCCC...",  // dark jacket shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: "#1e293b", T: color, W: "#fbbf24",
    },
  }
}

function portraitDennis(color: string): SpriteData {
  return {
    grid: [
      ".HHHHHHHH...",  // longer wavy hair top
      "HHHHHHHHH...",  // hair volume
      "HH.HHHH.HH.",  // hair framing face, past ears
      ".HGSSSSGH...",  // round glasses + face
      ".GSEESGBS...",  // glasses + eyes + bridge
      "..SsNNsS....",  // nose
      "..SSMMSS....",  // warm smile
      "...sSsS.....",  // chin
      ".YYWCCWYY...",  // orange headphones + collar
      ".CCCCCCCC...",  // light shirt shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: "#c49a6c", s: "#a07848", E: EYES, M: MOUTH, N: "#a07848",
      C: "#b0c4de", G: "#b08840", B: "#b08840", W: WHITE, Y: "#f97316",
    },
  }
}

function portraitAlbert(color: string): SpriteData {
  return {
    grid: [
      "............",
      "...HHHH.....",  // neat short hair
      "..HHHHHH....",  // hair
      "..HSSSSH....",  // face
      "..SEESEE....",  // warm eyes
      "..SsNNsS....",  // nose
      "..SSMMSS....",  // warm smile
      "...sSsS.....",  // chin
      "..WCCCCW....",  // polo collar
      ".CCCCCCCC...",  // shoulders
      "CCCCCCCCCC..",
      "............",
    ],
    palette: {
      H: HAIR_BLK, S: SKIN, s: SKIN_SHD, E: EYES, M: MOUTH, N: SKIN_SHD,
      C: color, W: WHITE,
    },
  }
}

function getPortraitForAgent(name: string, color: string): SpriteData {
  switch (name) {
    case "Commander": return portraitCommander(color)
    case "Sentry":  return portraitSentry(color)
    case "Forge":   return portraitForge(color)
    case "Cache":   return portraitCache(color)
    case "Vault":   return portraitVault(color)
    case "Gateway": return portraitGateway(color)
    case "Pixel":   return portraitPixel(color)
    case "Wrench":  return portraitWrench(color)
    case "Shield":  return portraitShield(color)
    case "Scope":   return portraitScope(color)
    case "Dennis":  return portraitDennis(color)
    case "Albert":  return portraitAlbert(color)
    default:        return portraitSentry(color)
  }
}

/**
 * Face-only pixel portrait — head, neck, and shoulders.
 * 12x12 grid with noses, chins, skin shadows, and more expression.
 */
function PixelPortrait({ name, color, size = 24 }: { name: string; color: string; size?: number }) {
  const { grid, palette } = getPortraitForAgent(name, color)
  const GRID = 12
  const cellSize = size / GRID

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`${name} portrait`}
    >
      <rect width={size} height={size} rx={size / 2} fill={color + "20"} />
      {grid.map((row, y) =>
        Array.from(row).map((ch, x) => {
          if (ch === "." || ch === " ") return null
          const fill = palette[ch]
          if (!fill) return null
          return (
            <rect
              key={`${y}-${x}`}
              x={x * cellSize}
              y={y * cellSize}
              width={cellSize}
              height={cellSize}
              fill={fill}
            />
          )
        })
      )}
    </svg>
  )
}

export { PixelAvatar, PixelPortrait }
export type { Agent }
